import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import authService from '../utils/authService';

const Breadcrumb = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);
  const user = authService.getUser();

  // Get user role and determine home dashboard
  const getUserRole = () => {
    const userRole = user?.role;
    if (userRole?.includes('ADMIN')) return 'admin';
    if (userRole?.includes('AGENT')) return 'agent';
    if (userRole?.includes('SUPPORT')) return 'support';
    return 'agent'; // default fallback
  };

  const getHomeDashboard = () => {
    const role = getUserRole();
    return `/${role}/dashboard`;
  };

  const breadcrumbNameMap = {
    // Role names - don't show these in breadcrumb, skip them
    'admin': null,
    'agent': null,
    'support': null,
    
    // Page names
    'dashboard': 'Dashboard',
    'searchhotel': 'Search Hotel',
    'search-hotel': 'Search Hotel',
    'searchhistory': 'Search History',
    'tokenrecharge': 'Token Recharge',
    'token-recharge': 'Token Recharge',
    'bookingdetails': 'Booking Details',
    'booking-details': 'Booking Details',
    // booking-details breadcrumb should always link to the list page
    // (handled via route redirect in App.jsx)
    'bookingcancellation': 'Booking Cancellation',
    'booking-cancellation': 'Booking Cancellation',
    'cancellation-booking': 'Cancellation Booking',
    'cancellation-page-details': 'Cancellation Details',
    'bookinghistory': 'Booking History',
    'booking-history': 'Booking History',
    'paymentinfo': 'Payment Information',
    'payment-info': 'Payment Information',
    'manualpayment': 'Manual Payment',
    'manual-payment': 'Manual Payment',
    'manual-payment-info': 'Manual Payment Info',
    'supportticket': 'Support Ticket',
    'support-tickets': 'Support Tickets',
    'support-ticket': 'Support Ticket',
    'tokenhistory': 'Token History',
    'token-history': 'Token History',
    'account-manager': 'Account Manager',
    'support-manager': 'Support Manager',
    'agent-tickets': 'Agent Tickets',
    'admin-assigned-tickets': 'My Support Tickets',
    'token-recharge-page': 'Token Recharge',
    'agent-token-recharge': 'Agent Token Recharge',
    'payment-refund-info': 'Payment Refund Info',
    'refund-info': 'Refund Info',
    'my-support-tickets': 'My Support Tickets',
    'manage-customers': 'Manage Customers',
    'add-virtual-account': 'Add Virtual Account',
    'settings': 'Settings',
    'api-manager': 'API Manager',
    'platform': 'Platform Settings',
    'cancellation-policy': 'Cancellation Policy',
    'details': 'Details',
    'recharge': 'Recharge',
    'prebook': 'Pre-booking',
    'customer-detail': 'Customer Details',
    'passenger-details': 'Passenger Details'
  };

  const getBreadcrumbName = (pathname) => {
    // Suppress dynamic ID segments (UUIDs, numeric IDs, alphanumeric booking IDs)
    if (/^[A-Z0-9]{6,}$/i.test(pathname) && !breadcrumbNameMap[pathname]) return null;
    const name = breadcrumbNameMap[pathname];
    if (name === null) return null; // Skip this segment
    return name || pathname.charAt(0).toUpperCase() + pathname.slice(1);
  };

  const getIcon = (pathname) => {
    const iconMap = {
      'dashboard': 'fas fa-tachometer-alt',
      'searchhotel': 'fas fa-search',
      'search-hotel': 'fas fa-search',
      'searchhistory': 'fas fa-history',
      'tokenrecharge': 'fas fa-coins',
      'token-recharge': 'fas fa-coins',
      'bookingdetails': 'fas fa-list-alt',
      'booking-details': 'fas fa-list-alt',
      'bookingcancellation': 'fas fa-times-circle',
      'booking-cancellation': 'fas fa-times-circle',
      'cancellation-booking': 'fas fa-times-circle',
      'cancellation-page-details': 'fas fa-times-circle',
      'bookinghistory': 'fas fa-clock',
      'booking-history': 'fas fa-clock',
      'paymentinfo': 'fas fa-credit-card',
      'payment-info': 'fas fa-credit-card',
      'manualpayment': 'fas fa-hand-holding-usd',
      'manual-payment': 'fas fa-hand-holding-usd',
      'manual-payment-info': 'fas fa-hand-holding-usd',
      'supportticket': 'fas fa-ticket-alt',
      'support-tickets': 'fas fa-ticket-alt',
      'support-ticket': 'fas fa-ticket-alt',
      'tokenhistory': 'fas fa-history',
      'token-history': 'fas fa-history',
      'account-manager': 'fas fa-users',
      'support-manager': 'fas fa-user-tie',
      'agent-tickets': 'fas fa-ticket-alt',
      'admin-assigned-tickets': 'fas fa-ticket-alt',
      'token-recharge-page': 'fas fa-coins',
      'agent-token-recharge': 'fas fa-coins',
      'payment-refund-info': 'fas fa-undo',
      'refund-info': 'fas fa-undo',
      'my-support-tickets': 'fas fa-ticket-alt',
      'manage-customers': 'fas fa-users',
      'add-virtual-account': 'fas fa-plus-circle',
      'settings': 'fas fa-cogs',
      'api-manager': 'fas fa-code',
      'platform': 'fas fa-server',
      'cancellation-policy': 'fas fa-file-contract',
      'details': 'fas fa-info-circle',
      'recharge': 'fas fa-plus',
      'prebook': 'fas fa-calendar-plus',
      'customer-detail': 'fas fa-user',
      'passenger-details': 'fas fa-users'
    };
    return iconMap[pathname] || 'fas fa-folder';
  };

  // Build breadcrumb items properly
  const buildBreadcrumbItems = () => {
    const items = [];
    const role = getUserRole();
    
    // Skip role segment and build from dashboard onwards
    let startIndex = 0;
    if (pathnames[0] === role) {
      startIndex = 1;
    }
    
    for (let i = startIndex; i < pathnames.length; i++) {
      const pathname = pathnames[i];
      const breadcrumbName = getBreadcrumbName(pathname);
      
      if (breadcrumbName !== null) {
        // Build the correct route path
        let routeTo;
        if (i === startIndex && pathname === 'dashboard') {
          // For dashboard, use the role-based path
          routeTo = `/${role}/dashboard`;
        } else {
          // For other paths, build from the role + segments up to current index
          const segments = pathnames.slice(0, i + 1);
          if (segments[0] !== role) {
            segments.unshift(role);
          }
          routeTo = `/${segments.join('/')}`;
        }
        
        items.push({
          name: breadcrumbName,
          path: routeTo,
          icon: getIcon(pathname)
        });
      }
    }
    
    // Set isLast on the final item that actually gets added
    if (items.length > 0) {
      items[items.length - 1].isLast = true;
    }
    
    return items;
  };

  const breadcrumbItems = buildBreadcrumbItems();

  return (
    <nav className="bg-white border-b border-gray-200 px-6 py-3">
      <ol className="flex items-center space-x-2 text-sm">
        <li>
          <Link to={getHomeDashboard()} className="text-gray-500 hover:text-gray-700 flex items-center">
            <i className="fas fa-home mr-1"></i>
            Dashboard
          </Link>
        </li>
        
        {breadcrumbItems.map((item, index) => {
          // Skip the first item if it's "Dashboard" since we already show it as Home
          if (index === 0 && item.name === 'Dashboard') {
            return null;
          }
          
          return (
            <li key={index} className="flex items-center">
              <i className="fas fa-chevron-right text-gray-400 mx-2"></i>
              {item.isLast ? (
                <span className="text-gray-900 font-medium flex items-center">
                  <i className={`${item.icon} mr-2 text-blue-600`}></i>
                  {item.name}
                </span>
              ) : (
                <Link 
                  to={item.path} 
                  className="text-gray-500 hover:text-gray-700 flex items-center"
                >
                  <i className={`${item.icon} mr-2`}></i>
                  {item.name}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;